import { useState, type ComponentProps, type ReactElement } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  Building2,
  EllipsisVertical,
  FileText,
  HandCoins,
  ShoppingCart,
  Trash2,
  User,
} from 'lucide-react'
import { toast } from 'sonner'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can, useAbility } from '@/lib/casl'
import {
  canManageCustomerBilling,
  canViewCustomerReceivables,
} from '@/lib/customer-receivables-access'
import dayjs from '@/lib/dayjs'
import { useSoftDeleteCustomerMutation } from '@/lib/services/customers/customers.query'
import { formatCurrency, getErrorMessage, parseCurrency } from '@/lib/utils'
import type {
  Customer,
  CustomerMetrics,
  CustomerStatement,
} from '@/lib/services/customers/customers.types'
import { RecordHistoryMenuItem } from '@/components/audit-log/RecordHistoryMenu'
import {
  AdminHeaderActionButton,
  AdminHeaderIconButton,
  AdminRecordHeader,
} from '@/components/patterns/admin-page'
import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { getCustomerReceivableInvoices } from './customer-invoice-display'

interface CustomerDetailHeaderProps {
  customer: Pick<Customer, 'id' | 'name' | 'is_business' | 'customer_since_on' | 'created_at'>
  receivables: Pick<CustomerStatement, 'invoices'> | null | undefined
  metrics: Pick<CustomerMetrics, 'lifetime_customer_value'>
  createPosCart: { isPending: boolean }
  onCreateOrder?: ComponentProps<typeof AdminHeaderActionButton>['onClick']
  onOpenPayment?: ComponentProps<typeof AdminHeaderActionButton>['onClick']
  onOpenStatement?: ComponentProps<typeof AdminHeaderActionButton>['onClick']
  isReadOnly: boolean
}

function CustomerMoreActionsMenu({
  customer,
  isReadOnly,
}: Pick<CustomerDetailHeaderProps, 'customer' | 'isReadOnly'>) {
  const ability = useAbility()
  const path = usePathHelper()
  const navigate = useNavigate()
  const deleteCustomerMutation = useSoftDeleteCustomerMutation({
    cleanupOnSuccess: false,
  })
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const canDeleteCustomer = !isReadOnly && ability.can('delete', 'customers')
  const canViewHistory = ability.can('view', 'audit-logs')
  const historyLink = customer?.id ? path.customers.link('$customerId/history', customer.id) : null

  const deleteCustomer = async () => {
    try {
      await deleteCustomerMutation.mutateAsync(customer.id)
      toast.success('Customer deleted successfully')
      await navigate({
        ...path.customers.index().link,
        ignoreBlocker: true,
        replace: true,
      })
      void Promise.resolve()
        .then(() => deleteCustomerMutation.cleanupDeletedCustomer?.(customer.id))
        .catch(() => undefined)
    } catch (error) {
      toast.error('Failed to delete customer', {
        description: getErrorMessage(error),
      })
    }
  }

  if (!customer?.id || (!canDeleteCustomer && !canViewHistory)) {
    return null
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <AdminHeaderIconButton icon={EllipsisVertical} label="More customer actions" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" size="sm">
          <RecordHistoryMenuItem link={historyLink} />
          {canDeleteCustomer ? (
            <>
              {canViewHistory ? <DropdownMenuSeparator /> : null}
              <DropdownMenuItem variant="destructive" onSelect={() => setIsDeleteDialogOpen(true)}>
                <Trash2 className="size-4" />
                Delete customer
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      {canDeleteCustomer ? (
        <ConfirmationDialog
          trigger={null}
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          variant="destructive"
          title="Delete Customer"
          description="Are you sure you want to delete this customer?"
          confirmText="Delete"
          pendingText="Deleting..."
          isPending={deleteCustomerMutation.isPending}
          onConfirm={deleteCustomer}
        />
      ) : null}
    </>
  )
}

export function CustomerDetailHeader({
  customer,
  receivables,
  metrics,
  createPosCart,
  onCreateOrder,
  onOpenPayment,
  onOpenStatement,
  isReadOnly,
}: CustomerDetailHeaderProps): ReactElement {
  const ability = useAbility()
  const openInvoices = getCustomerReceivableInvoices(receivables)
  const hasOpenInvoices = openInvoices.length > 0
  const canViewStatement = canViewCustomerReceivables(ability) && hasOpenInvoices
  const canManageBillingActions = canManageCustomerBilling(ability) && hasOpenInvoices
  const lifetimeCustomerValue = parseCurrency(metrics.lifetime_customer_value ?? 0)
  const customerSinceValue = customer.customer_since_on || customer.created_at
  const customerSince = customerSinceValue ? dayjs(customerSinceValue).format('MMM YYYY') : null

  return (
    <AdminRecordHeader
      icon={customer.is_business ? Building2 : User}
      iconTone="mint"
      title={customer.name || `Customer #${customer.id}`}
      subtitle={customerSince ? `Customer since ${customerSince}` : 'Customer profile'}
      badges={
        lifetimeCustomerValue > 0 ? (
          <Badge variant="secondary" size="status">
            Lifetime value {formatCurrency(lifetimeCustomerValue)}
          </Badge>
        ) : null
      }
      actions={
        <>
          <Can do="create" on="sales-orders">
            <AdminHeaderActionButton onClick={onCreateOrder} disabled={createPosCart.isPending}>
              <ShoppingCart className="size-4" />
              Create Order
            </AdminHeaderActionButton>
          </Can>
          {canManageBillingActions ? (
            <Can do="edit" on="customers">
              <AdminHeaderActionButton type="button" variant="outline" onClick={onOpenPayment}>
                <HandCoins className="size-4" />
                Take Payment
              </AdminHeaderActionButton>
            </Can>
          ) : null}
          {canViewStatement ? (
            <AdminHeaderActionButton
              type="button"
              variant="outline"
              aria-label="Statement"
              onClick={onOpenStatement}
            >
              <FileText className="size-4" />
              Statement
            </AdminHeaderActionButton>
          ) : null}
          <CustomerMoreActionsMenu customer={customer} isReadOnly={isReadOnly} />
        </>
      }
    />
  )
}
